import { faceOf, type ScoreBreakdown, type WireSeat, type WireState } from '@nt2/engine';
import { clear, el, on } from './dom.js';

export type PanelTab = 'players' | 'feed';

/**
 * The side panel: who else is at the table, and what just happened.
 *
 * On a wide screen it sits beside the board; on a phone it slides in over it.
 * Everything here is read-only apart from choosing whose grid to watch.
 */
export interface PanelActions {
  onWatch(seatId: string | null): void;
  onLeave(): void;
  onEndMatch(): void;
  onConcede(): void;
}

export class SidePanel {
  readonly root: HTMLElement;
  readonly scrim: HTMLElement;
  #body: HTMLElement;
  #tabs: HTMLElement;
  #tab: PanelTab = 'players';
  #open = false;
  /** Last state rendered, so switching tabs can redraw without waiting for
   *  the next update from the authority. */
  #last: { wire: WireState; watching: string | null } | null = null;

  constructor(private acts: PanelActions) {
    this.#body = el('div', { class: 'side__body scroll' });
    this.#tabs = el('div', { class: 'side__tabs', role: 'tablist' });
    for (const [id, label] of [['players', 'Players'], ['feed', 'Activity']] as const) {
      const b = el('button', {
        class: 'side__tab', type: 'button', role: 'tab',
        'aria-selected': String(this.#tab === id), text: label,
      });
      on(b, 'click', () => this.setTab(id));
      this.#tabs.append(b);
    }
    this.root = el('aside', { class: 'side', 'data-open': 'false' }, this.#tabs, this.#body);
    this.scrim = el('div', { class: 'scrim', 'data-open': 'false' });
    on(this.scrim, 'click', () => this.toggle(false));
  }

  setTab(tab: PanelTab): void {
    this.#tab = tab;
    [...this.#tabs.children].forEach((b, i) =>
      b.setAttribute('aria-selected', String((i === 0 ? 'players' : 'feed') === tab)));
    if (this.#last) this.render(this.#last.wire, this.#last.watching);
  }

  toggle(open?: boolean): void {
    this.#open = open ?? !this.#open;
    this.root.dataset.open = String(this.#open);
    this.scrim.dataset.open = String(this.#open);
  }

  render(wire: WireState, watching: string | null): void {
    this.#last = { wire, watching };
    clear(this.#body);
    if (this.#tab === 'players') this.#renderPlayers(wire, watching);
    else this.#renderFeed(wire);
  }

  #renderPlayers(wire: WireState, watching: string | null): void {
    // The phone layout drops these from the top bar, so they live here where
    // there is room. Harmless duplication on a wide screen.
    if (wire.phase !== 'lobby') {
      const elapsed = wire.startedAt ? Date.now() - wire.startedAt : 0;
      const secs = Math.round(elapsed / 1000);
      this.#body.append(el('div', { class: 'wordlist', style: 'margin-bottom:10px' },
        el('span', { class: 'wordlist__w', text: `${wire.bagCount} in bag` }),
        el('span', { class: 'wordlist__w', text: `${wire.roundsLeft} rounds left` }),
        el('span', {
          class: 'wordlist__w',
          text: `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')} played`,
        })));
    }
    /*
     * Which table this is. Not something anyone types — the link does the
     * joining — but when two people on a call are trying to work out whether
     * they are in the same game, "table K7P2QX" settles it. Solo play has no
     * table to name.
     */
    if (wire.matchId && wire.matchId !== 'solo') {
      this.#body.append(el('div', {
        class: 'setting__hint tablecode',
        title: 'Everyone in the same match sees the same table code.',
      }, el('span', { text: 'Table ' }),
         el('b', { class: 'tablecode__id', text: wire.matchId.toUpperCase() })));
    }
    const list = el('div', { class: 'seats' });
    const order = [...wire.seats].sort((a, b) => {
      const ar = a.rackCount < 0 ? 99 : a.rackCount;
      const br = b.rackCount < 0 ? 99 : b.rackCount;
      return (a.finishedAt ? 0 : 1) - (b.finishedAt ? 0 : 1) || ar - br;
    });
    for (const seat of order) {
      const mine = seat.id === wire.youSeatId;
      const canWatch = seat.grid !== null || wire.config.visibility.grids === 'onRequest';
      const names = seat.members
        .map((m) => wire.players.find((p) => p.id === m)?.name ?? '?')
        .join(' + ');
      const card = el('button', {
        class: 'seatcard' + (seat.connectedMembers === 0 ? ' seatcard--offline' : '')
          + (seat.finishedAt ? ' seatcard--done' : ''),
        type: 'button',
        'aria-current': String(watching === seat.id),
        title: canWatch ? 'Watch this grid' : 'This match keeps grids private',
      });
      const dot = el('span', { class: 'seatcard__dot' });
      dot.style.background = seat.colour;
      card.append(dot);
      card.append(el('span', { class: 'seatcard__name' },
        el('span', { text: seat.name + (mine ? ' (you)' : '') }),
        seat.members.length > 1
          ? el('div', { class: 'seatcard__team', text: names }) : null));
      if (seat.stuck) {
        card.append(el('span', { class: 'chip chip--warn seatcard__stuck', text: 'stuck' }));
      }
      card.append(el('span', {
        class: 'seatcard__meta',
        text: seat.finishedAt ? 'done'
          : seat.rackCount < 0 ? '—'
          : `${seat.rackCount}`,
        title: 'Letters left in hand',
      }));
      if (canWatch) on(card, 'click', () => this.acts.onWatch(watching === seat.id ? null : seat.id));
      else card.disabled = true;
      list.append(card);
    }
    this.#body.append(list);

    if (wire.stuckCount > 0 && wire.phase === 'playing') {
      this.#body.append(el('div', {
        class: 'setting__hint', style: 'margin-top:10px;color:var(--warn)',
        text: `${wire.stuckCount} of ${wire.stuckNeeded} say they are stuck. ` +
              'When everyone does, the whole table draws.',
      }));
    }

    // Anyone watching rather than playing.
    const spectators = wire.players.filter((p) => p.isSpectator);
    if (spectators.length) {
      this.#body.append(el('div', {
        class: 'setting__hint', style: 'margin-top:12px',
        text: `Watching: ${spectators.map((s) => s.name).join(', ')}`,
      }));
    }

    // Every match needs a door marked exit. Without one the only way out of a
    // game is to close the tab, which loses the table for everybody else too.
    const exits = el('div', { class: 'panel__exits' });
    const isHost = wire.players.some((p) => p.isHost && p.seatId === wire.youSeatId);
    const mySeat = wire.seats.find((s) => s.id === wire.youSeatId);
    if (wire.phase === 'playing' && mySeat && mySeat.finishedAt === null
        && wire.seats.length > 1) {
      const concede = el('button', {
        class: 'btn btn--sm btn--ghost', type: 'button', text: 'Bow out of this match',
        title: 'Stop playing but stay and watch. The others carry on.',
      });
      on(concede, 'click', () => this.acts.onConcede());
      exits.append(concede);
    }
    if (wire.phase === 'playing' && isHost) {
      const endIt = el('button', {
        class: 'btn btn--sm btn--danger', type: 'button', text: 'End the match for everyone',
        title: 'Stops the match now and scores it on what is on the boards.',
      });
      on(endIt, 'click', () => this.acts.onEndMatch());
      exits.append(endIt);
    }
    const leave = el('button', {
      class: 'btn btn--sm btn--ghost', type: 'button', text: 'Leave the table',
      title: 'Go back to the start. Your seat is released.',
    });
    on(leave, 'click', () => this.acts.onLeave());
    exits.append(leave);
    this.#body.append(exits);

    if (wire.config.visibility.rackLetters) {
      this.#body.append(el('div', { class: 'setting__hint', style: 'margin-top:12px' },
        el('b', { text: 'Open hands: ' }),
        el('span', { text: 'every rack is visible in this match.' })));
      for (const seat of wire.seats) {
        if (!seat.rack || seat.id === wire.youSeatId) continue;
        this.#body.append(el('div', { style: 'margin:6px 0' },
          el('div', { class: 'setting__hint', text: seat.name }),
          el('div', { class: 'wordlist' },
            ...seat.rack.map((t) => el('span', {
              class: 'wordlist__w', text: faceOf(t) ?? '★',
            })))));
      }
    }
  }

  #renderFeed(wire: WireState): void {
    const feed = el('div', { class: 'feed' });
    for (const entry of [...wire.log].reverse()) {
      feed.append(el('div', { class: 'feed__row' },
        el('span', { class: 'feed__time', text: clockOf(entry.at, wire.startedAt) }),
        el('span', { text: entry.text })));
    }
    if (!wire.log.length) {
      feed.append(el('div', { class: 'setting__hint', text: 'Nothing has happened yet.' }));
    }
    this.#body.append(feed);
  }
}

function clockOf(at: number, startedAt: number | null): string {
  if (!startedAt) return '';
  const s = Math.max(0, Math.round((at - startedAt) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export interface ResultRow {
  seatId: string;
  name: string;
  tilesPlaced: number;
  tilesInHand: number;
  score: number;
  complete: boolean;
  /** This seat used every letter and stopped the match, taking the bonus. */
  wentOut?: boolean;
  /** Null when the table never revealed this grid, so it cannot be scored. */
  breakdown?: ScoreBreakdown | null;
}

/**
 * Final table, shown when the match ends.
 *
 * Ranked by score, so the row order is the result. Tapping a row shows how
 * that player's score was made up — the same sum the player themselves
 * watched — which is the difference between being told you lost and seeing
 * where it went.
 */
export function resultsPanel(
  wire: WireState,
  rows: ResultRow[],
  onInspect?: (seatId: string) => void,
): HTMLElement {
  const box = el('div', { class: 'results' });
  rows.forEach((r, i) => {
    const seat = wire.seats.find((s) => s.id === r.seatId);
    const win = wire.winnerSeatId === r.seatId;
    const line = el('div', { class: 'result' + (win ? ' result--win' : '') });
    line.append(el('span', { class: 'result__rank', text: win ? '🏆' : `${i + 1}` }));
    // Always present, so the row keeps its five columns whatever is known.
    const dot = el('span', { class: 'seatcard__dot' });
    if (seat) dot.style.background = seat.colour;
    line.append(dot);
    line.append(el('span', {},
      el('b', { text: r.name + (r.seatId === wire.youSeatId ? ' (you)' : '') }),
      el('div', {
        class: 'setting__hint',
        text: r.breakdown === null ? 'Grid kept private'
          : r.wentOut ? 'Used every letter — took the bonus'
          : r.complete ? 'Finished with a valid grid'
          : `${r.tilesInHand} letter${r.tilesInHand === 1 ? '' : 's'} left in hand`,
      })));
    line.append(el('span', { class: 'result__num', text: `${r.tilesPlaced} placed` }));
    line.append(el('span', { class: 'result__num', text: `${r.score} pts` }));
    if (onInspect && r.breakdown !== null) {
      line.classList.add('result--tappable');
      line.tabIndex = 0;
      line.title = `Show how ${r.name} scored`;
      on(line, 'click', () => onInspect(r.seatId));
      on(line, 'keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInspect(r.seatId); }
      });
    }
    box.append(line);
  });
  return box;
}

/**
 * The running totals for everyone who has played at this table.
 *
 * Shown once there is more than one match to add up — after the first there
 * is nothing a total says that the match table does not. Sorted by points,
 * with wins beside them, and the viewer's own line marked.
 */
export function seriesPanel(wire: WireState): HTMLElement | null {
  const series = wire.series;
  if (!series || series.games < 2) return null;
  const rows = Object.entries(series.totals).sort(([, a], [, b]) => b.points - a.points || b.wins - a.wins);
  const box = el('div', { class: 'series' },
    el('div', { class: 'series__head', text: `Totals after ${series.games} games at this table` }));
  rows.forEach(([id, t], i) => {
    box.append(el('div', { class: 'series__row' + (id === wire.youPlayerId ? ' series__row--you' : '') },
      el('span', { class: 'series__rank', text: String(i + 1) }),
      el('span', { class: 'series__name', text: t.name + (id === wire.youPlayerId ? ' (you)' : '') }),
      el('span', { class: 'series__wins', text: `${t.wins} win${t.wins === 1 ? '' : 's'}` }),
      el('span', { class: 'series__pts', text: `${t.points}` })));
  });
  return box;
}
